import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const HorseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [horse, setHorse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [insDoc, setInsDoc] = useState(null);

  useEffect(() => {
    api.get(`/horses/${id}`).then(r => setHorse(r.data)).catch(() => toast.error('Failed to load horse')).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    api.get('/documents').then(r => {
      const docs = Array.isArray(r.data) ? r.data : (r.data.documents || []);
      const d = docs.filter(x => x.document_type === 'insurance' && x.horse_id === id)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
      setInsDoc(d || null);
    }).catch(() => {});
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${horse.name} from your horses?`)) return;
    try {
      await api.delete(`/horses/${id}`);
      toast.success(`${horse.name} removed`);
      navigate('/horses');
    } catch { toast.error('Failed to remove horse'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!horse) return <div className="page-content"><p>Horse not found</p></div>;

  const infoRows = [
    ...(horse.date_of_birth ? [['Date of birth', horse.date_of_birth]] : []),
    ...(horse.sire ? [['Sire', horse.sire]] : []),
    ...(horse.dam ? [['Dam', horse.dam]] : []),
    ...((horse.blanket_size || horse.blanket_color) ? [['Blanket', [horse.blanket_size, horse.blanket_color].filter(Boolean).join(' \u00b7 ')]] : []),
    ...((horse.sheet_size || horse.sheet_color) ? [['Sheet', [horse.sheet_size, horse.sheet_color].filter(Boolean).join(' \u00b7 ')]] : []),
    ['Breed', horse.breed], ['Age', horse.age ? `${horse.age} years` : null],
    ['Gender', horse.gender], ['Color', horse.color], ['Markings', horse.markings],
    ['Discipline', horse.discipline], ['Height', horse.height ? `${horse.height} hh` : null],
    ['Barn', horse.barn_name], ['Location', horse.barn_location],
    ['Registration', horse.registration_number], ['Microchip', horse.microchip],
  ].filter(([, v]) => v);

  const careRows = [
    ['Insurance', insDoc ? ([insDoc.issued_by, insDoc.provider_phone].filter(Boolean).join(' \u00b7 ') || 'On file') : 'None on file'],
    ['Vet', horse.vet_name], ['Vet phone', horse.vet_phone],
    ['Farrier', horse.farrier_name], ['Farrier phone', horse.farrier_phone],
    ['Farrier interval', horse.farrier_interval_weeks ? `Every ${horse.farrier_interval_weeks} weeks` : null],
  ].filter(([, v]) => v);

  return (
    <div>
      <div style={{ background: 'var(--cr-primary)', paddingBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px 0' }}>
          <button onClick={() => navigate('/horses')} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20 }}>‹</button>
          <h1 style={{ color: 'white', flex: 1, fontSize: 17 }}>{horse.name}</h1>
          <button onClick={() => navigate(`/horses/${id}/edit`)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 12, borderRadius: 6, padding: '4px 10px' }}>Edit</button>
        </div>
        <div style={{ display: 'flex', gap: 12, padding: '16px 16px 20px', alignItems: 'flex-end' }}>
          <div className="avatar avatar-xl" style={{ background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)' }}>
            {horse.avatar_url ? <img src={horse.avatar_url} alt={horse.name} /> : <span style={{ fontSize: 36 }}>🐴</span>}
          </div>
          <div>
            <div style={{ color: 'white', fontSize: 15, fontWeight: 600 }}>{horse.name}</div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, marginTop: 2 }}>
              {[horse.age ? `${horse.age}yo` : null, horse.breed, horse.gender].filter(Boolean).join(' · ')}
            </div>
            {horse.discipline && <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 2 }}>{horse.discipline}</div>}
            {horse.barn_name && <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 }}>📍 {horse.barn_name}</div>}
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* Quick actions */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { icon: '💉', label: 'Health', path: `/horses/${id}/health` },
            { icon: '💊', label: 'Feed & Supplements', path: `/horses/${id}/supplements` },
            { icon: '🧴', label: 'Supplies', path: `/horses/${id}/supplies` },
            { icon: '🖨️', label: 'Stall card', path: `/horses/${id}/stall-card` },
            { icon: '📅', label: 'Calendar', path: `/calendar/add?horse=${id}` },
            { icon: '📁', label: 'Docs', path: `/documents?horse=${id}` },
            { icon: '🔔', label: 'Remind', path: `/reminders` },
            ...((horse.gender === 'mare' || horse.gender === 'filly')
              ? [{ icon: '🐣', label: 'Breeding', path: `/horses/${id}/breeding` }]
              : []),
          ].map(a => (
            <button key={a.label} onClick={() => navigate(a.path)}
              style={{ background: 'white', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '12px 6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 20 }}>{a.icon}</span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{a.label}</span>
            </button>
          ))}
        </div>

        {horse.bio && (
          <>
            <div className="section-label">Bio</div>
            <div className="card"><p style={{ color: 'var(--text-primary)', margin: 0, fontSize: 13 }}>{horse.bio}</p></div>
          </>
        )}

        {infoRows.length > 0 && (
          <>
            <div className="section-label" onClick={() => setDetailsOpen(o => !o)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
              <span>Details</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 14, transform: detailsOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>›</span>
            </div>
            {detailsOpen && (
              <div className="card" style={{ padding: '6px 14px' }}>
                {infoRows.map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color)', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                    <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{val}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {careRows.length > 0 && (
          <>
            <div className="section-label">Care team</div>
            <div className="card" style={{ padding: '6px 14px' }}>
              {careRows.map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                  <span style={{ fontWeight: 500 }}>{val}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {horse.feed_notes && (
          <>
            <div className="section-label">Feed & supplements</div>
            <div className="card"><p style={{ fontSize: 13, color: 'var(--text-primary)', margin: 0 }}>{horse.feed_notes}</p></div>
          </>
        )}

        <button className="btn btn-danger btn-full" onClick={handleDelete} style={{ marginTop: 16 }}>Remove {horse.name}</button>
      </div>
    </div>
  );
};

export default HorseDetailPage;
